import { ref, watch } from 'vue'
import {
  EMPTY_FC,
  LAYER_CATEGORIES,
  LAYER_OWNERS,
} from '@/composables/gtfs/map/gtfsMapLayers.js'
import {
  isochroneAdminFc,
  isochroneCoverageFc,
  isochroneFeatures,
  restoreIsochroneMap,
  snapshotIsochroneMap,
} from '@/composables/gtfs/map/isochroneMapState.js'
import { ensureOwnerAnalysisLayers } from '@/composables/gtfs/map/registerAnalysisLayerCatalog.js'
import { applyViewModeVisibility } from '@/composables/gtfs/map/useAnalysisViewModeSync.js'
import { useMapLayerStore } from '@/stores/mapLayer.store'
import {
  isAnalysisOwnerActive,
  registerAnalysisRestore,
  useAnalysisWorkspaceStore,
} from '@/stores/analysisWorkspace.store'

/** Analiz panelleri ↔ harita katmanları */
export const catchmentGeojson = ref({ type: 'FeatureCollection', features: [] })
export const corridorGeojson = ref({ type: 'FeatureCollection', features: [] })
export const serviceAvailabilityStopsGeojson = ref({
  type: 'FeatureCollection',
  features: [],
})
export const serviceAvailabilityBuffersGeojson = ref({
  type: 'FeatureCollection',
  features: [],
})
export const transitDesertGeojson = ref({
  type: 'FeatureCollection',
  features: [],
})
/** SA belediye özeti — mahalle choropleth */
export const adminMahalleGeojson = ref({
  type: 'FeatureCollection',
  features: [],
})
/** İlçe sınırları (SA + Transit Desert ortak) */
export const adminIlceGeojson = ref({
  type: 'FeatureCollection',
  features: [],
})

/** Katarlanma: haritada dikdörtgen alan seçimi */
export const corridorAreaMode = ref(false)
/** { west, south, east, north } | null */
export const corridorAreaBbox = ref(null)
export const corridorAreaGeojson = ref({ type: 'FeatureCollection', features: [] })

export const isochroneMode = ref(false)
/** @deprecated tek profil — isochroneProfiles kullanın */
export const isochroneProfile = ref('transit')
/** Seçili ulaşım modları (çoklu) */
export const isochroneProfiles = ref(['walk', 'transit', 'drive'])
export const isochroneMinutes = ref(20)
/** Süre katmanları — örn. 10 / 20 / 30 dk */
export const isochroneMinuteBands = ref([10, 20, 30])
/** HH:mm — kalkış / varış saati (İstanbul) */
export const isochroneDepartTime = ref('08:00')
/** true: buraya kim gelebilir (arriveBy); false: buradan nereye */
export const isochroneArriveBy = ref(false)
/** Haritada gösterilecek profil / süre filtreleri */
export const isoVisibleProfiles = ref(['walk', 'transit', 'drive'])
export const isoVisibleMinutes = ref([10, 20, 30])
export const clearIsochronesSignal = ref(0)

/** Harita ↔ sağ panel: yükleme / hata / son kapsama */
export const isoLoading = ref(false)
export const isoError = ref('')
export const isochroneLayerCount = ref(0)
export const lastIsochroneCoverage = ref(null)

/** Transit OTP altyapısı: null = üretim stack */
export { selectedNetworkStackId } from '@/composables/gtfs/useNetworkStacks.js'

/** Harita katman görünürlüğü */
export const mapLayerStops = ref(true)
export const mapLayerRoutes = ref(true)
export const mapLayerIsochrone = ref(true)
export const mapLayerCorridor = ref(true)
export const mapLayerCorridorArea = ref(true)
export const mapLayerCatchment = ref(true)
export const mapLayerServiceAvailability = ref(true)
/** Servis alanı (buffer poligon) — teknik detay; varsayılan kapalı */
export const mapLayerSaBuffers = ref(false)
/** Transit desert choropleth */
export const mapLayerTransitDesert = ref(true)
/** Mahalle hizmet kapsama choropleth */
export const mapLayerAdminCoverage = ref(true)
/** İlçe sınırları + etiket */
export const mapLayerIlceBoundaries = ref(true)
/** Analiz modunda düz GTFS duraklarını soluklaştır */
export const mapDimGtfsStops = ref(false)

/**
 * Katarlanma / izokron / catchment / service availability iken
 * durak tıklaması sağ paneli “Duraklar”a çevirmesin.
 */
export const suppressStopPanelNav = ref(false)

export function setCatchmentGeojson(fc) {
  catchmentGeojson.value = fc || { type: 'FeatureCollection', features: [] }
}

export function setCorridorGeojson(fc) {
  corridorGeojson.value = fc || { type: 'FeatureCollection', features: [] }
}

export function setServiceAvailabilityStops(fc) {
  serviceAvailabilityStopsGeojson.value =
    fc || { type: 'FeatureCollection', features: [] }
}

export function setServiceAvailabilityBuffers(fc) {
  serviceAvailabilityBuffersGeojson.value =
    fc || { type: 'FeatureCollection', features: [] }
}

export function setTransitDesertGeojson(fc) {
  transitDesertGeojson.value =
    fc || { type: 'FeatureCollection', features: [] }
}

export function setAdminMahalleGeojson(fc) {
  adminMahalleGeojson.value =
    fc || { type: 'FeatureCollection', features: [] }
}

export function setAdminIlceGeojson(fc) {
  adminIlceGeojson.value =
    fc || { type: 'FeatureCollection', features: [] }
}

export function bboxToPolygonFc(bbox) {
  if (!bbox) return { type: 'FeatureCollection', features: [] }
  const { west, south, east, north } = bbox
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [west, south],
              [east, south],
              [east, north],
              [west, north],
              [west, south],
            ],
          ],
        },
      },
    ],
  }
}

export function setCorridorAreaBbox(bbox) {
  corridorAreaBbox.value = bbox
  corridorAreaGeojson.value = bboxToPolygonFc(bbox)
}

export function clearCorridorArea() {
  corridorAreaBbox.value = null
  corridorAreaGeojson.value = { type: 'FeatureCollection', features: [] }
}

/** Sadece çizimi kaldır; bbox (yeniden analiz için) kalsın */
export function clearCorridorAreaVisual() {
  corridorAreaGeojson.value = { type: 'FeatureCollection', features: [] }
}

export function setCorridorAreaMode(on) {
  corridorAreaMode.value = !!on
  if (on) {
    isochroneMode.value = false
  }
}

export function setIsochroneMode(on) {
  isochroneMode.value = !!on
  if (on) {
    corridorAreaMode.value = false
  }
  if (!on) isoError.value = ''
}

export function resetIsochroneCoverage() {
  lastIsochroneCoverage.value = null
  isochroneLayerCount.value = 0
  isoError.value = ''
}

function isoSnapshotHasData(snap) {
  if (!snap) return false
  return (
    (snap.features || []).length > 0 ||
    (snap.coverage?.features || []).length > 0 ||
    (snap.administrative?.features || []).length > 0
  )
}

/** Pasif Erişim'in poligonları. clearLegacy harita ref'lerini silse de bu kalır. */
let isoSnapshot = null

watch(
  [isochroneFeatures, isochroneCoverageFc, isochroneAdminFc],
  () => {
    const snap = snapshotIsochroneMap()
    if (!isoSnapshotHasData(snap) && !isAnalysisOwnerActive(LAYER_OWNERS.isochrone)) {
      return
    }
    isoSnapshot = snap
  },
  { deep: true }
)

function asFeatureCollection(fc) {
  if (fc?.type === 'FeatureCollection') return fc
  return { type: 'FeatureCollection', features: [] }
}

/**
 * Erişim'e dönüş: izokron poligonları, dissolve ve mahalle katmanı.
 * Görünürlük yalnızca sahip aktifken açılır.
 */
export function restoreIsochroneAnalysisMap() {
  if (!isAnalysisOwnerActive(LAYER_OWNERS.isochrone)) return
  const live = snapshotIsochroneMap()
  const snap = isoSnapshotHasData(live) ? live : isoSnapshot
  if (!isoSnapshotHasData(snap)) return
  restoreIsochroneMap(snap)
  mapLayerIsochrone.value = true
  const detail = {
    type: 'FeatureCollection',
    features: [...(snap.features || [])],
  }
  try {
    ensureOwnerAnalysisLayers(LAYER_OWNERS.isochrone)
    const store = useMapLayerStore()
    store.setSourceData(
      LAYER_CATEGORIES.analysis,
      LAYER_OWNERS.isochrone,
      'detail',
      detail
    )
    store.setSourceData(
      LAYER_CATEGORIES.analysis,
      LAYER_OWNERS.isochrone,
      'coverage',
      asFeatureCollection(snap.coverage)
    )
    store.setSourceData(
      LAYER_CATEGORIES.analysis,
      LAYER_OWNERS.isochrone,
      'administrative',
      asFeatureCollection(snap.administrative)
    )
    const ws = useAnalysisWorkspaceStore()
    applyViewModeVisibility(LAYER_OWNERS.isochrone, ws.effectiveViewMode)
  } catch {
    /* harita yokken katman yazımı sessiz */
  }
  isoVisibleProfiles.value = [...(isoVisibleProfiles.value || [])]
  isoVisibleMinutes.value = [...(isoVisibleMinutes.value || [])]
}

let isoRestoreRegistered = false

export function registerIsochroneAnalysisRestore() {
  if (isoRestoreRegistered) return
  registerAnalysisRestore(LAYER_OWNERS.isochrone, restoreIsochroneAnalysisMap)
  isoRestoreRegistered = true
}

export { EMPTY_FC }
